import { FPlusCodeContainer, FPlusCodeEditor } from '@festina-life/fplus'
import type * as monaco from 'monaco-editor'
import { useMemo, useRef, useState } from 'react'

import type { FPlusExpressionValue } from '../../types/templateModel'
import { useFPlusCodeEditorContext } from '../../uiModels/fplus/useFPlusCodeEditorContext'
import {
  functionBody,
  functionDeclaration,
} from '../templateFieldsTable/functionHelpers'
import { FPlusInlineEditContainer } from './FPlusInlineEditContainer'
import type { FPlusEditorProps } from './types'

type Props = FPlusEditorProps & {
  onCancel: () => void
  initialHeightPx: number
  isTransparent?: boolean
}

export const FPlusInlineEditor = ({
  code,
  fallbackCode,
  expressionPath,
  onCancel,
  onChangeCode,
  initialHeightPx,
  isTransparent = true,
  simulatedFunctionDeclaration,
}: Props) => {
  const initialCode = code || fallbackCode || ''
  const initialHeader = useMemo(
    () => functionDeclaration(initialCode),
    [initialCode],
  )
  const functionBodyOrSelf = useMemo(
    () => functionBody(initialCode),
    [initialCode],
  )

  if (initialHeader && simulatedFunctionDeclaration) {
    throw new Error(
      'Simulated functions declarations cannot be used on function based inline editors.',
    )
  }

  const languageServerContext = useFPlusCodeEditorContext({
    expressionPath,
    prefixFunctionDeclaration: simulatedFunctionDeclaration ?? initialHeader,
  })

  const [editedCode, setEditedCode] = useState(functionBodyOrSelf)

  const editorOptions = useMemo(() => {
    // It's important to keep editor options out of the React render cycles in order to avoid unwanted rerenders of the
    // FPlusTextEditor component, given that editor options are passed in as dependency to useEffect hooks.
    const editorOptions: monaco.editor.IStandaloneEditorConstructionOptions = {
      wordWrap: 'on',
      lineNumbers: 'off',
      glyphMargin: false,
      lineDecorationsWidth: 0,
      scrollBeyondLastLine: false,
      fixedOverflowWidgets: true,
      fontSize: 11,
      renderLineHighlight: 'none',
      folding: false,
      lineNumbersMinChars: 0,
      rulers: undefined,
      overviewRulerLanes: 0,
      contextmenu: false,
      hideCursorInOverviewRuler: true,
      overviewRulerBorder: false,
      minimap: { enabled: false },
      padding: { top: 0, bottom: 0 },
      lineHeight: 14,
    }

    return editorOptions
  }, [])

  // It's important to maintain a stable and single reference for the code changed handler to avoid issues with duplicated
  // monaco instances in the FPlusCodeEditor
  const handleCodeChanged = useRef((nextValue: FPlusExpressionValue) => {
    onChangeCode?.(
      initialHeader
        ? {
            ...nextValue,
            fplusSource: `${initialHeader} =\n    ${nextValue.fplusSource}`,
          }
        : nextValue,
    )
  })

  return (
    <div className="size-full">
      <FPlusInlineEditContainer
        code={editedCode}
        initialHeightPx={initialHeightPx}
        onCancel={onCancel}
        onChangeCode={handleCodeChanged.current}
      >
        <FPlusCodeContainer
          isTransparent={isTransparent}
          className="size-full min-h-[inherit]"
        >
          <FPlusCodeEditor
            initialCode={functionBodyOrSelf}
            editorInput={languageServerContext}
            onChangeCode={setEditedCode}
            options={editorOptions}
            isSizedByContent
          />
        </FPlusCodeContainer>
      </FPlusInlineEditContainer>
    </div>
  )
}
